import { useCallback, useEffect, useState } from 'react';
import { useLockBodyScroll } from '@/utils/useLockBodyScroll';
import { useEmailRecommendedCouponPopup } from '@/data/Content/EmailRecommendedCouponPopup';
import { useEmailRecommendedCouponPopupRecommendation } from '@/data/Content/EmailRecommendedCouponPopupRecommendation';
import { AuthentificationFlow } from '@/components/blocks/AuthentificationFlow';
import { PopupDialog } from '@/components/blocks/EmailRecommendedCouponPopup/parts/PopupDialog';
import { EmailRecommendedCouponPopupProps } from '@/components/blocks/EmailRecommendedCouponPopup/parts/types';

const DEFAULT_TITLE = 'PIEZAS SELECCIONADAS PARA TI';
const DEFAULT_DESCRIPTION = 'Selecciona al menos dos productos para aprovechar tu cupón';

export const EmailRecommendedCouponPopup: React.FC<EmailRecommendedCouponPopupProps> = ({
	partNumbers,
	emsName,
	title = DEFAULT_TITLE,
	description = DEFAULT_DESCRIPTION,
	open,
	onClose,
}) => {
	const isControlled = open !== undefined;
	const [internalOpen, setInternalOpen] = useState(false);

	const handleCloseCommon = useCallback(() => {
		if (!isControlled) {
			setInternalOpen(false);
		}
		onClose?.();
	}, [isControlled, onClose]);

	const { content: couponContent } = useEmailRecommendedCouponPopupRecommendation(emsName);

	const {
		products,
		productsSelected,
		isLoadingProducts,
		isLoadingAvailability,
		onToggleCheckProduct,
		onSelectChildrenProduct,
		onAddToCart,
		onGoToCheckout,
		openAuthCheckoutFlow,
		onAuthFlowClose,
		onAuthSuccess,
		onGoToHome,
	} = useEmailRecommendedCouponPopup({
		partNumbers,
		couponCode: couponContent?.couponCode,
		onClose: handleCloseCommon,
	});

	const handleClose = useCallback(() => {
		handleCloseCommon();
		onGoToHome();
	}, [isControlled, onClose, onGoToHome]);

	const isLoading = isLoadingProducts || isLoadingAvailability;
	const hasProducts = isLoading || products.length > 0;

	useEffect(() => {
		if (!isControlled && hasProducts) {
			setInternalOpen(true);
		}
	}, [isControlled, hasProducts]);

	const visible =
		(isControlled ? Boolean(open) : internalOpen) && Boolean(couponContent) && hasProducts;

	const showPopup = visible && !openAuthCheckoutFlow;
	useLockBodyScroll(showPopup);

	return (
		<>
			<PopupDialog
				open={showPopup}
				loading={isLoading}
				skeletonCount={partNumbers?.length || 3}
				title={title}
				description={description}
				couponCode={couponContent?.couponCode ?? ''}
				couponLabel={couponContent?.couponLabel ?? ''}
				couponExpiry={couponContent?.couponExpiry ?? ''}
				products={products}
				productsSelected={productsSelected}
				onClose={handleClose}
				onToggleCheckProduct={onToggleCheckProduct}
				onSelectChildrenProduct={onSelectChildrenProduct}
				onAddToCart={onAddToCart}
				onGoToCheckout={onGoToCheckout}
			/>

			<AuthentificationFlow
				open={openAuthCheckoutFlow}
				type="login"
				origin="quick_cart"
				onClose={onAuthFlowClose}
				onAuthSuccess={onAuthSuccess}
			/>
		</>
	);
};
